import { useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

import AiSpendFilterBar from '../../../components/AiSpend/AiSpendFilterBar';
import { getAiSpendFilterOptions } from '../../../services/aiSpendService';
import useApiResource from '../../../utils/hooks/useApiResource';
import { AI_SPEND_BASE, AI_SPEND_PAGES, findPageByPathname } from './aiSpendPages';
import useAiSpendFilters from './useAiSpendFilters';

export default function AiSpendLayout() {
  const location = useLocation();
  const { filters, setFilters, clearFilters, queryParams, queryKey, activeFilterCount } =
    useAiSpendFilters();
  const page = findPageByPathname(location.pathname);

  // Resolve the default range into the URL once, so the link an admin copies
  // always says exactly which period it shows.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('date_from') || !params.get('date_to')) {
      setFilters(
        { date_from: filters.date_from, date_to: filters.date_to },
        { replace: true, keepPage: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: options, loading: optionsLoading } = useApiResource(
    ({ signal }) =>
      getAiSpendFilterOptions({
        params: { date_from: filters.date_from, date_to: filters.date_to },
        signal,
      }),
    { deps: [`${filters.date_from}:${filters.date_to}`] },
  );

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <header className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
          <Link
            to="/admin/organizations"
            className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Platform administration
          </Link>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
              Platform administration
            </p>
            <h1 className="mt-1 text-3xl font-bold text-gray-950 dark:text-white">
              AI spend
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
              Every paid AI call across OpenAI, ElevenLabs, HeyGen and LiveAvatar.
              Observation only — nothing here blocks or limits anything.
            </p>
          </div>
          <nav aria-label="AI spend" className="mt-5 flex gap-1 overflow-x-auto">
            {AI_SPEND_PAGES.map(({ path, label }) => (
              <NavLink
                key={path}
                to={{ pathname: `${AI_SPEND_BASE}/${path}`, search: location.search }}
                className={({ isActive }) =>
                  `whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold ${
                    isActive
                      ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800 dark:text-gray-300'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="mx-auto max-w-7xl border-t border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-6 lg:px-8">
          <AiSpendFilterBar
            fields={page.filters}
            filters={filters}
            setFilters={setFilters}
            options={options}
            optionsLoading={optionsLoading}
            activeFilterCount={activeFilterCount}
            onClear={clearFilters}
          />
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8">
        <Outlet
          context={{ filters, setFilters, queryParams, queryKey, options, optionsLoading }}
        />
      </main>
    </div>
  );
}
